import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Filter, X, Plus } from "lucide-react";
import ProductFormModal from "../components/ProductFormModal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Catalog() {
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const [products, setProducts] = useState([]);
	const [filters, setFilters] = useState({
		category: searchParams.get("category") || "all",
		subcategory: searchParams.get("subcategory") || "all",
		search: searchParams.get("search") || "",
		availability: searchParams.get("availability") || "all",
	});
	const [showFilters, setShowFilters] = useState(false);

	// модалка формы товара (add/edit)
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [formMode, setFormMode] = useState("add"); // 'add' | 'edit'
	const [currentProduct, setCurrentProduct] = useState(null);

	// модалка удаления
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const [deleteProduct, setDeleteProduct] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const isAdmin = user?.role?.toLowerCase() === "admin";

	useEffect(() => {
		fetchProducts();
	}, [filters]);

	const fetchProducts = async () => {
		try {
			const params = {};
			if (filters.category && filters.category !== "all")
				params.category = filters.category;
			if (filters.subcategory && filters.subcategory !== "all")
				params.subcategory = filters.subcategory;
			if (filters.search) params.search = filters.search;
			if (filters.availability && filters.availability !== "all")
				params.availability = filters.availability;

			const response = await axios.get(`${API}/products`, { params });
			setProducts(response.data);
		} catch (error) {
			console.error("Failed to fetch products:", error);
		}
	};

	const handleFilterChange = (key, value) => {
		const newFilters = { ...filters, [key]: value };
		setFilters(newFilters);

		const params = {};
		Object.keys(newFilters).forEach((k) => {
			if (newFilters[k]) params[k] = newFilters[k];
		});
		setSearchParams(params);
	};

	const clearFilters = () => {
		setFilters({
			category: "all",
			subcategory: "all",
			search: "",
			availability: "all",
		});
		setSearchParams({});
	};

	const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
		if (key === "search") return value !== "";
		return value !== "all";
	});

	// ========== ADMIN POPUPS ==========

	const openAddModal = () => {
		setFormMode("add");
		setCurrentProduct(null);
		setIsFormOpen(true);
	};

	const openEditModal = (product) => {
		setFormMode("edit");
		setCurrentProduct(product);
		setIsFormOpen(true);
	};

	const openDeleteModal = (product) => {
		setDeleteProduct(product);
		setIsDeleteOpen(true);
	};

	const closeDeleteModal = () => {
		setIsDeleteOpen(false);
		setDeleteProduct(null);
	};

	const confirmDeleteProduct = async () => {
		if (!deleteProduct) return;
		try {
			setDeleting(true);
			await axios.delete(`${API}/products/${deleteProduct.id}`);
			toast.success("Товар удалён");
			setProducts((prev) => prev.filter((p) => p.id !== deleteProduct.id));
			closeDeleteModal();
		} catch (error) {
			console.error("Failed to delete product:", error);
			toast.error("Не удалось удалить товар");
		} finally {
			setDeleting(false);
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 py-24">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h1
							className="text-4xl font-bold mb-2"
							style={{ fontFamily: "Cormorant Garamond, serif" }}
							data-testid="catalog-title"
						>
							Каталог товаров
						</h1>
						<p className="text-gray-600">Найдено товаров: {products.length}</p>
					</div>

					{isAdmin && (
						<Button
							onClick={openAddModal}
							className="self-start"
							data-testid="add-product-button"
						>
							<Plus className="w-4 h-4 mr-2" />
							Добавить товар
						</Button>
					)}
				</div>

				{/* Filters */}
				<div className="mb-8">
					<Button
						onClick={() => setShowFilters(!showFilters)}
						data-testid="toggle-filters-button"
						variant="outline"
						className="lg:hidden mb-4"
					>
						<Filter className="w-5 h-5 mr-2" />
						Фильтры
					</Button>

					<div
						className={`bg-white p-6 rounded-xl shadow-md ${
							showFilters ? "block" : "hidden lg:block"
						}`}
						data-testid="filters-panel"
					>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
							<div>
								<label className="block text-sm font-medium mb-2">
									Категория
								</label>
								<Select
									value={filters.category}
									onValueChange={(v) => handleFilterChange("category", v)}
								>
									<SelectTrigger data-testid="category-filter">
										<SelectValue placeholder="Все категории" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">Все категории</SelectItem>
										<SelectItem value="Рыба">Рыба</SelectItem>
										<SelectItem value="Морепродукты">Морепродукты</SelectItem>
										<SelectItem value="Моллюски">Моллюски</SelectItem>
										<SelectItem value="Икра">Икра</SelectItem>
										<SelectItem value="Консервы">Консервы</SelectItem>
										<SelectItem value="Трюфели">Трюфели</SelectItem>
										<SelectItem value="Сопутствующие товары">
											Сопутствующие товары
										</SelectItem>
									</SelectContent>
								</Select>
							</div>

							{filters.category === "Рыба" && (
								<div>
									<label className="block text-sm font-medium mb-2">Тип</label>
									<Select
										value={filters.subcategory}
										onValueChange={(v) => handleFilterChange("subcategory", v)}
									>
										<SelectTrigger data-testid="subcategory-filter">
											<SelectValue placeholder="Все типы" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="all">Все типы</SelectItem>
											<SelectItem value="свежая">Свежая</SelectItem>
											<SelectItem value="замороженная">Замороженная</SelectItem>
											<SelectItem value="копчёная">Копчёная</SelectItem>
											<SelectItem value="солёная">Солёная</SelectItem>
										</SelectContent>
									</Select>
								</div>
							)}

							<div>
								<label className="block text-sm font-medium mb-2">
									Наличие
								</label>
								<Select
									value={filters.availability}
									onValueChange={(v) => handleFilterChange("availability", v)}
								>
									<SelectTrigger data-testid="availability-filter">
										<SelectValue placeholder="Все" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">Все</SelectItem>
										<SelectItem value="есть">В наличии</SelectItem>
										<SelectItem value="ожидаем">Ожидается</SelectItem>
									</SelectContent>
								</Select>
							</div>

							{hasActiveFilters && (
								<div className="flex items-end">
									<Button
										onClick={clearFilters}
										data-testid="clear-filters-button"
										variant="outline"
										className="w-full"
									>
										<X className="w-4 h-4 mr-2" />
										Очистить
									</Button>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Products Grid */}
				{products.length > 0 ? (
					<div
						className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
						data-testid="products-grid"
					>
						{products.map((product) => (
							<ProductCard
								key={product.id}
								product={product}
								isAdmin={isAdmin}
								onDelete={() => openDeleteModal(product)}
								onEdit={() => openEditModal(product)}
							/>
						))}
					</div>
				) : (
					<div className="text-center py-20" data-testid="no-products">
						<p className="text-2xl text-gray-500">Товары не найдены</p>
						{hasActiveFilters && (
							<Button onClick={clearFilters} className="mt-4">
								Сбросить фильтры
							</Button>
						)}
					</div>
				)}
			</div>

			{/* Модалка add/edit */}
			{isAdmin && (
				<ProductFormModal
					open={isFormOpen}
					mode={formMode}
					product={currentProduct}
					onClose={() => setIsFormOpen(false)}
					onSaved={() => {
						// после сохранения обновляем список
						fetchProducts();
					}}
				/>
			)}

			{/* Модалка delete */}
			{isAdmin && (
				<DeleteConfirmModal
					open={isDeleteOpen}
					product={deleteProduct}
					onCancel={closeDeleteModal}
					onConfirm={confirmDeleteProduct}
					loading={deleting}
				/>
			)}
		</div>
	);
}

// =========================
// DeleteConfirmModal внутри того же файла
// =========================
function DeleteConfirmModal({ open, product, onCancel, onConfirm, loading }) {
	if (!open || !product) return null;

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onCancel && onCancel();
		}
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
			onClick={handleOverlayClick}
		>
			<div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
				<h2 className="text-lg font-semibold mb-2">Удалить товар?</h2>
				<p className="text-sm text-gray-600 mb-6">
					Вы действительно хотите удалить{" "}
					<span className="font-semibold">«{product.name}»</span>? Это действие
					нельзя будет отменить.
				</p>

				<div className="flex justify-end gap-3">
					<Button variant="outline" onClick={onCancel} disabled={loading}>
						Отмена
					</Button>
					<Button
						variant="destructive"
						onClick={onConfirm}
						disabled={loading}
						className="bg-rose-600 hover:bg-rose-700 text-white"
					>
						{loading ? "Удаление..." : "Удалить"}
					</Button>
				</div>
			</div>
		</div>
	);
}
